/**
 * #380: Eine Seite für beide Richtungen — Verteiler wählen und Verteiler verlassen.
 *
 * Vorher gab es dafür zwei Seiten am selben Token: eine trug aus, eine hakte an und ab.
 * Zwei fast gleiche Links in einer Fusszeile sind eine Zumutung, also sind sie
 * zusammengelegt. `/abbestellen/:token` und `/verteiler/:token` führen beide hierher.
 *
 * **Der laute Weg zuerst.** Wer den Link anklickt, will meistens raus — nicht
 * konfigurieren. Kam die Mail über einen bestimmten Verteiler (`?v=`), steht dessen
 * Ausstieg ganz oben und ist mit einem Klick erledigt. Die feine Auswahl steht darunter
 * für die, die sie wollen.
 *
 * **Ohne Anmeldung**, weil ein grosser Teil der Adressen Menschen ohne Portal-Konto
 * gehört. Und **erst auf Klick**, nie beim blossen Öffnen: Mailprogramme und Virenscanner
 * rufen Links im Hintergrund auf.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MdCheck, MdError, MdMarkEmailRead, MdUnsubscribe } from 'react-icons/md';
import { lightTheme } from '../helfer/types';
import PublicBrandHeader, { PublicLegalFooter } from './PublicChrome';
import VerteilerAngaben, { Angaben, LEERE_ANGABEN } from './VerteilerAngaben';

interface VerteilerZeile {
  id: number;
  name: string;
  description: string | null;
  mitglied: boolean;
}

const theme = lightTheme;

const VerteilerPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const herkunftId = Number(params.get('v')) || null;

  const [status, setStatus] = useState<'laedt' | 'offen' | 'fehler'>('laedt');
  const [fehler, setFehler] = useState('');
  const [email, setEmail] = useState('');
  const [zeilen, setZeilen] = useState<VerteilerZeile[]>([]);
  const [speichert, setSpeichert] = useState(false);
  const [meldung, setMeldung] = useState('');
  // #388: Die eigenen Angaben. Hier darf die Person sie auch **aendern** — es ist ihre
  // Zeile, und sie kennt ihren Ortsverein besser als jeder Import. Bei der Anmeldung ist
  // das anders, dort ist die Adresse noch unbestaetigt.
  const [angaben, setAngaben] = useState<Angaben>(LEERE_ANGABEN);

  const laden = useCallback(async () => {
    if (!token) { setStatus('fehler'); setFehler('Dieser Link ist unvollständig.'); return; }
    try {
      const res = await axios.get(`/api/email-verteiler/einstellungen/${token}`);
      setEmail(res.data.data.email);
      setZeilen(res.data.data.verteiler);
      setAngaben({ ...LEERE_ANGABEN, ...(res.data.data.angaben ?? {}) });
      setStatus('offen');
    } catch (e) {
      setFehler((e as { response?: { data?: { error?: string } } }).response?.data?.error
        || 'Dieser Link ist ungültig oder abgelaufen.');
      setStatus('fehler');
    }
  }, [token]);

  useEffect(() => { laden(); }, [laden]);

  const speichern = async (neue: VerteilerZeile[], text: string) => {
    setSpeichert(true);
    setMeldung('');
    try {
      await axios.put(`/api/email-verteiler/einstellungen/${token}`, {
        verteilerIds: neue.filter(z => z.mitglied).map(z => z.id),
        angaben,
      });
      setZeilen(neue);
      setMeldung(text);
    } catch {
      setMeldung('Das hat nicht geklappt. Bitte versuche es erneut.');
    } finally {
      setSpeichert(false);
    }
  };

  const umschalten = (id: number) =>
    setZeilen(z => z.map(v => (v.id === id ? { ...v, mitglied: !v.mitglied } : v)));

  const herkunft = herkunftId ? zeilen.find(z => z.id === herkunftId && z.mitglied) : null;

  const karte: React.CSSProperties = {
    width: '100%', maxWidth: 560, background: theme.bgCard, borderRadius: 'var(--r-l)',
    border: `1px solid ${theme.border}`, padding: 26,
  };
  const knopf = (art: 'primary' | 'ghost' | 'danger'): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    padding: '10px 18px', borderRadius: 'var(--r-m)', fontSize: '.9rem', fontWeight: 600,
    fontFamily: 'inherit', cursor: speichert ? 'default' : 'pointer',
    border: art === 'ghost' ? `1px solid ${theme.border}` : 'none',
    background: art === 'primary' ? theme.accent : art === 'danger' ? theme.danger : 'transparent',
    color: art === 'ghost' ? theme.text : '#fff',
  });

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh',
      padding: '24px 16px', background: theme.bg,
    }}>
      <PublicBrandHeader />
      <div style={karte}>
        {status === 'laedt' && <p style={{ color: theme.textMuted, margin: 0, textAlign: 'center' }}>Einen Moment …</p>}

        {status === 'fehler' && (
          <div style={{ textAlign: 'center' }}>
            <MdError size={44} color={theme.danger} />
            <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '12px 0 8px' }}>
              Das hat nicht geklappt
            </h1>
            <p style={{ color: theme.textMuted, fontSize: '.9rem', lineHeight: 1.6, margin: 0 }}>{fehler}</p>
          </div>
        )}

        {status === 'offen' && (
          <>
            <h1 style={{ fontSize: '1.2rem', fontWeight: 700, color: theme.text, margin: '0 0 2px' }}>
              Deine E-Mail-Verteiler
            </h1>
            <p style={{ fontSize: '.85rem', color: theme.textMuted, margin: '0 0 18px' }}>{email}</p>

            {/* Der laute Weg zuerst: Wer den Link anklickt, will meistens nur raus. */}
            {herkunft && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '13px 14px', marginBottom: 16, borderRadius: 'var(--r-l)',
                background: `${theme.danger}12`, border: `1px solid ${theme.danger}55`,
              }}>
                <span style={{ fontSize: '.87rem', color: theme.text, flex: 1, minWidth: 170, lineHeight: 1.5 }}>
                  Du wurdest über <strong>{herkunft.name}</strong> angeschrieben.
                </span>
                <button
                  type="button"
                  disabled={speichert}
                  onClick={() => speichern(
                    zeilen.map(z => (z.id === herkunft.id ? { ...z, mitglied: false } : z)),
                    `Du wurdest aus „${herkunft.name}" ausgetragen.`
                  )}
                  style={knopf('danger')}
                >
                  Aus diesem Verteiler austragen
                </button>
              </div>
            )}

            <p style={{ fontSize: '.86rem', color: theme.textMuted, margin: '0 0 12px', lineHeight: 1.55 }}>
              {herkunft ? 'Oder wähle genauer aus, worüber du informiert werden möchtest:' : 'Wähle aus, worüber du informiert werden möchtest.'}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {zeilen.map(z => (
                <label
                  key={z.id}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 11, padding: '12px 14px',
                    borderRadius: 'var(--r-l)', cursor: 'pointer',
                    border: `1px solid ${z.mitglied ? theme.accent : theme.border}`,
                    background: z.mitglied ? `${theme.accent}0f` : theme.bgCard,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={z.mitglied}
                    onChange={() => umschalten(z.id)}
                    style={{ marginTop: 2, accentColor: theme.accent, width: 16, height: 16 }}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: '.9rem', fontWeight: 600, color: theme.text }}>{z.name}</span>
                    {z.description && (
                      <span style={{ fontSize: '.79rem', color: theme.textMuted, lineHeight: 1.45 }}>
                        {z.description}
                      </span>
                    )}
                  </span>
                </label>
              ))}
              {zeilen.length === 0 && (
                <p style={{ fontSize: '.87rem', color: theme.textMuted, margin: 0 }}>
                  Du stehst derzeit in keinem Verteiler.
                </p>
              )}
            </div>

            {meldung && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginTop: 16,
                padding: '11px 13px', borderRadius: 'var(--r-m)', fontSize: '.87rem', color: theme.text,
                background: `${theme.success}15`, border: `1px solid ${theme.success}55`,
              }}>
                <MdCheck size={17} color={theme.success} /> {meldung}
              </div>
            )}

            {/* #388: Wer schon im Pool steht, kann seine Angaben hier nachtragen —
                sonst bleibt eine Zeile, die die Pflege nie findet. */}
            <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${theme.border}` }}>
              <h2 style={{ fontSize: '.95rem', fontWeight: 600, color: theme.text, margin: '0 0 4px' }}>
                Deine Angaben
              </h2>
              <p style={{ fontSize: '.8rem', color: theme.textMuted, margin: '0 0 14px', lineHeight: 1.6 }}>
                Damit wir dich in der Rundmail ansprechen können und die richtigen
                Nachrichten bei dir landen. Wird zusammen mit deiner Auswahl gespeichert.
              </p>
              <VerteilerAngaben werte={angaben} onChange={setAngaben} />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18, flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={speichert}
                onClick={() => speichern(zeilen.map(z => ({ ...z, mitglied: false })), 'Du bekommst keine Rundmails mehr.')}
                style={knopf('ghost')}
              >
                <MdUnsubscribe size={17} /> Aus allen austragen
              </button>
              <button type="button" disabled={speichert} onClick={() => speichern(zeilen, 'Gespeichert.')} style={knopf('primary')}>
                <MdMarkEmailRead size={17} /> Speichern
              </button>
            </div>

            <p style={{ fontSize: '.78rem', color: theme.textMuted, margin: '16px 0 0', lineHeight: 1.6 }}>
              Persönliche Nachrichten wie Anmeldebestätigungen oder Einteilungen bekommst du
              weiterhin — die hängen nicht an einem Verteiler.
            </p>
          </>
        )}
      </div>
      <PublicLegalFooter />
    </div>
  );
};

export default VerteilerPage;
